/**
 * Share helpers — branded canvas share-cards + native share sheet with
 * clipboard fallback. Used by workout results, streak milestones, challenge
 * links, invites and profile sharing.
 *
 * Returns one of: "shared" | "copied" | "aborted" | "failed" so callers can
 * toast appropriately ("aborted" = user closed the sheet, say nothing).
 */

const BG = "#0c0d11";
const CARD = "#171922";
const GREEN = "#34E08A";
const GOLD = "#F3CA5F";
const TX_DIM = "#8b91a0";

function drawCard({ big, unit, sub, footer }) {
    const size = 1080;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");

    // Background + faint grid
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, size, size);
    ctx.strokeStyle = "rgba(255,255,255,0.035)";
    ctx.lineWidth = 1;
    for (let p = 90; p < size; p += 90) {
        ctx.beginPath(); ctx.moveTo(p, 0); ctx.lineTo(p, size); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(0, p); ctx.lineTo(size, p); ctx.stroke();
    }

    // Inner card
    const m = 70;
    ctx.fillStyle = CARD;
    ctx.strokeStyle = "rgba(255,255,255,0.09)";
    ctx.lineWidth = 2;
    roundRect(ctx, m, m, size - 2 * m, size - 2 * m, 28);
    ctx.fill();
    ctx.stroke();

    // Wordmark
    ctx.fillStyle = GREEN;
    ctx.fillRect(m + 56, m + 58, 10, 44);
    ctx.fillStyle = "#fff";
    ctx.font = "700 52px 'JetBrains Mono', monospace";
    ctx.textBaseline = "top";
    ctx.fillText("nerdy_", m + 86, m + 56);

    // Big metric
    ctx.fillStyle = GREEN;
    ctx.font = "900 300px 'Brockmann', system-ui, sans-serif";
    ctx.textBaseline = "alphabetic";
    const bigStr = String(big);
    const bw = ctx.measureText(bigStr).width;
    ctx.shadowColor = "rgba(52,224,138,0.45)";
    ctx.shadowBlur = 60;
    ctx.fillText(bigStr, (size - bw) / 2, 600);
    ctx.shadowBlur = 0;

    // Unit
    ctx.fillStyle = "#fff";
    ctx.font = "800 64px 'Brockmann', system-ui, sans-serif";
    const uw = ctx.measureText(unit.toUpperCase()).width;
    ctx.fillText(unit.toUpperCase(), (size - uw) / 2, 690);

    // Sub line
    if (sub) {
        ctx.fillStyle = TX_DIM;
        ctx.font = "500 36px 'JetBrains Mono', monospace";
        const sw = ctx.measureText(sub).width;
        ctx.fillText(sub, (size - sw) / 2, 765);
    }

    // Footer
    ctx.fillStyle = GOLD;
    ctx.font = "700 34px 'JetBrains Mono', monospace";
    ctx.fillText(footer || "nerdy.fit", m + 56, size - m - 64);
    ctx.fillStyle = TX_DIM;
    ctx.font = "500 30px 'JetBrains Mono', monospace";
    const tag = "camera-counted";
    const tw = ctx.measureText(tag).width;
    ctx.fillText(tag, size - m - 56 - tw, size - m - 64);

    return canvas;
}

function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

export async function shareLink({ title, text, url }) {
    if (navigator.share) {
        try {
            await navigator.share({ title, text, url });
            return "shared";
        } catch (e) {
            if (e.name === "AbortError") return "aborted";
        }
    }
    try {
        await navigator.clipboard.writeText(url ? `${text} ${url}` : text);
        return "copied";
    } catch {
        return "failed";
    }
}

export async function shareImageCard({ big, unit, sub, footer, text, url }) {
    try {
        const canvas = drawCard({ big, unit, sub, footer });
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
        if (blob && navigator.canShare) {
            const file = new File([blob], "nerdy-workout.png", { type: "image/png" });
            if (navigator.canShare({ files: [file] })) {
                try {
                    await navigator.share({ files: [file], text: text || "" });
                    return "shared";
                } catch (e) {
                    if (e.name === "AbortError") return "aborted";
                }
            }
        }
    } catch (e) {
        console.warn("[share] card generation failed:", e?.message || e);
    }
    return shareLink({ text, url });
}
